'use strict';

import moment from 'moment';
import 'moment/locale/ru';
import Property from './property/property';
import Author from '../user';
import Contact from '../common/contact';
import ResidentialComplex from './residentialComplex';
import GardenAssociation from './gardenAssociation';
import CottageComplex from './cottageComplex';
import GarageComplex from './garageComplex';
import DealCondition from './dealCondition';
import DataSource from './dataSource';
import { Price, PriceHistory, Deal } from '../finance';
import Service from '../finance/service';
import Permission from '../common/permission';
import { List } from 'immutable';
import { LANDS } from '../../consts';

/**
 * Модель объявлений
 * @param object объект объявления
 * @param dictionary объект словаря
 * @constructor
 */
function Announcement (object, dictionary) {
  this.dealType = object.get('dealType');
  this.realtyType = object.get('realtyType');
  this.description = object.get('description');
  this.inFavorites = object.get('inFavorites');

  this.property = new Property(object, dictionary);
  this.author = new Author(object);
  this.contact = new Contact(object);
  this.permissions = new Permission(object.get('permissions'));
  this.services = Service.make(object.get('services'));

  this.dealCondition = new DealCondition(object, dictionary);

  const priceHistory = object.get('priceHistory');
  this.priceHistory = priceHistory ? new PriceHistory(priceHistory) : null;

  const dataSource = object.get('dataSource');
  this.dataSource = dataSource ? new DataSource(dataSource, dictionary) : null;

  const comments = object.get('comments');
  this.comment = comments && comments.size ? comments.get(0) : null;

  this.deal = object.get('deal') ? new Deal(object.get('deal')) : null;
  this.price = new Price(object.get('price'));
  this.pricePerAre = object.get('pricePerAre') ? new Price(object.get('pricePerAre')) : null;
  this.pricePerSquareMeter = object.get('pricePerSquareMeter') ? new Price(object.get('pricePerSquareMeter')) : null;

  const photos = object.getIn(['photos', 'all']) || new List();
  this.photos = photos.sort((a, b) => b.get('isMain') - a.get('isMain'));
  this.mainPhoto = object.get('mainPhoto') || object.getIn(['photos', 'main', 'url']);

  this.uri = object.get('uri');
  this.editPath = object.get('editPath', '');

  const rc = object.get('residentialComplex');
  this.residentialComplex = rc ? new ResidentialComplex(rc) : null;

  const ga = object.get('gardenAssociation');
  this.gardenAssociation = ga ? new GardenAssociation(ga) : null;

  const township = object.get('township');
  this.cottageComplex = township ? new CottageComplex(township) : null;

  const cooperative = object.get('cooperative');
  this.garageComplex = cooperative ? new GarageComplex(cooperative) : null;

  this.createdDate = moment.unix(object.get('inserted'));
  this.createdDate.locale('ru');

  this.updatedDate = moment.unix(object.get('updated'));
  this.updatedDate.locale('ru');

  this.breadcrumbs = object.getIn(['meta', 'breadcrumbs']) || new List();

  this.hasCallTrackingPhones = object.get('hasCallTrackingPhones');
}
const proto = Announcement.prototype;

proto.getId = function () {
  return this.getProperty().getId();
};

proto.getComment = function () {
  return this.comment;
};

proto.getDataSource = function () {
  return this.dataSource;
};

proto.getDealType = function () {
  return this.dealType;
};
proto.getRealtyType = function () {
  return this.realtyType;
};

proto.getPermissions = function () {
  return this.permissions;
};

proto.getResidentialComplex = function () {
  return this.residentialComplex;
};

proto.getGardenAssociation = function () {
  return this.gardenAssociation;
};

proto.getCottageComplex = function () {
  return this.cottageComplex;
};

proto.getGarageComplex = function () {
  return this.garageComplex;
};

proto.getDealCondition = function () {
  return this.dealCondition;
};

proto.getLink = function () {
  return this.uri;
};

proto.getEditLink = function () {
  return this.editPath;
};

proto.getPhotos = function () {
  return this.photos;
};

proto.getMainPhoto = function () {
  return this.mainPhoto;
};

/**
 * Объект недвижимости
 * @returns {Property}
 */
proto.getProperty = function () {
  return this.property;
};

/**
 * Автор объявления
 * @returns {User}
 */
proto.getAuthor = function () {
  return this.author;
};

/**
 * Контакты
 * @returns {Contact}
 */
proto.getContact = function () {
  return this.contact;
};

proto.getDeal = function () {
  return this.deal;
};

/**
 * Цена объявления
 * @returns {Price}
 */
proto.getPrice = function () {
  return this.price;
};

proto.getPricePerAre = function () {
  return this.pricePerAre;
};

proto.getPricePerSquareMeter = function () {
  return this.pricePerSquareMeter;
};

/**
 * Описание объявления
 * @returns {*}
 */
proto.getDescription = function () {
  return this.description;
};

/**
 * Список сервисов
 * @returns {*}
 */
proto.getServices = function () {
  return this.services;
};

proto.isPremium = function () {
  return this.getServices().filter(v => v.id === 1).count() || false;
};

proto.isPromo = function () {
  return this.getServices().filter(v => v.id === 2).count() || false;
};

proto.getCreatedDate = function () {
  return this.createdDate;
};

proto.getUpdatedDate = function () {
  return this.updatedDate;
};

proto.getBreadcrumbs = function () {
  return this.breadcrumbs;
};

/**
 * История изменения цены
 * @returns {PriceHistory|null}
 */
proto.getPriceHistory = function () {
  return this.priceHistory;
};

proto.getAreaPriceType = function () {
  const type = this.getProperty().getType();

  if (this.getRealtyType() === LANDS && [1, 4, 35, 36].indexOf(type.get('id')) !== -1) {
    return Price.PER_AR;
  }

  return Price.PER_SQUARE_METER;
};

proto.isFavorite = function () {
  return this.inFavorites;
};

proto.isCallTrackingPhones = function () {
  return this.hasCallTrackingPhones;
};

export default Announcement;



// WEBPACK FOOTER //
// ./src/classes/announcement/announcement.js